import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { HeartPulse, Tv, LineChart, Building } from 'lucide-react';

/* ─── 4 Pilares de Valor de Hospital Status ──────────────────────────── */
const features = [
  {
    id: 1,
    title: 'Visibilidad quirúrgica para familias',
    description:
      'Las familias siguen cada etapa desde su celular. Cero llamadas a recepción, cero ansiedad. Tu hospital proyecta control y empatía.',
    variant: 'top' as const,
    icon: HeartPulse,
    accentColor: 'var(--color-emerald-500)',
    badgeClass: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    badgeLabel: 'Pacientes & Familias',
  },
  {
    id: 2,
    title: 'Pantallas inteligentes con monetización integrada',
    description:
      'Las pantallas de sala de espera no solo informan — generan ingresos. Programa anuncios internos o vende espacios a terceros con analytics incluidos.',
    variant: 'left' as const,
    icon: Tv,
    accentColor: 'var(--color-med-accent)',
    badgeClass: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    badgeLabel: 'CFO & Finanzas',
  },
  {
    id: 3,
    title: 'Inteligencia operativa ejecutiva',
    description:
      'Eficiencia por quirófano, heatmaps de ocupación, rankings de cirujanos. Métricas que antes no existían y que ahora informan decisiones de alto impacto.',
    variant: 'right' as const,
    icon: LineChart,
    accentColor: 'var(--color-amber-500)',
    badgeClass: 'bg-amber-50 text-amber-700 border-amber-200',
    badgeLabel: 'CEO & Dirección',
  },
  {
    id: 4,
    title: 'Presencia digital de primer nivel',
    description:
      'Branding institucional en cada pantalla. Layouts premium que elevan la percepción de tu hospital ante pacientes, familias y aseguradoras.',
    variant: 'left' as const,
    icon: Building,
    accentColor: 'var(--color-med-accent)',
    badgeClass: 'bg-slate-100 text-slate-700 border-slate-200',
    badgeLabel: 'Marketing & Marca',
  },
];

/* ─── Componente Principal ──────────────────────────────────────────── */
export function FeaturesIsometric() {
  const [activeId, setActiveId] = useState<number>(1);

  return (
    <section className="w-full bg-gradient-to-b from-white to-med-bg pt-8 pb-16 md:pt-12 md:pb-28 relative overflow-hidden">
      {/* Decorative Background */}
      <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-med-accent/8 rounded-full blur-[120px] pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-emerald-200/15 rounded-full blur-[100px] pointer-events-none"></div>

      <div className="mx-auto w-full max-w-6xl px-6 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
          className="mb-14 text-center"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-med-light/80 mb-6 shadow-sm">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-med-accent opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-med-accent"></span>
            </span>
            <span className="text-[10px] font-bold text-med-navy/60 uppercase tracking-widest font-heading">
              4 Pilares de Valor
            </span>
          </div>

          <h2 className="text-3xl md:text-5xl font-bold text-med-navy font-heading mb-5 tracking-[-0.025em] leading-[1.15]">
            Cuatro pilares.{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-med-accent to-indigo-500">
              Un solo sistema.
            </span>
          </h2>
          <p className="max-w-2xl mx-auto text-lg text-med-navy/50 font-normal leading-[1.6]">
            Cada funcionalidad fue diseñada para resolver un problema operativo concreto y generar retorno medible.
          </p>
        </motion.div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 items-center gap-12 md:grid-cols-2">
          {/* Left — Feature Cards */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="flex flex-col gap-2"
          >
            {features.map((feature) => (
              <FeatureCard
                key={feature.id}
                feature={feature}
                isActive={activeId === feature.id}
                onClick={() => setActiveId(feature.id)}
              />
            ))}
          </motion.div>

          {/* Right — Stacked Isometric Boxes */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex items-center justify-center"
          >
            <StackedIsometricBoxes activeId={activeId} />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ─── Feature Card ──────────────────────────────────────────────────── */
function FeatureCard({
  feature,
  isActive,
  onClick,
}: {
  feature: (typeof features)[0];
  isActive: boolean;
  onClick: () => void;
}) {
  const Icon = feature.icon;

  return (
    <div
      onClick={onClick}
      className={`z-20 cursor-pointer rounded-2xl p-5 transition-all duration-300 ${
        isActive
          ? 'bg-white shadow-[0_8px_30px_rgb(0,0,0,0.06)] ring-1 ring-slate-200/60'
          : 'hover:bg-white/60'
      }`}
    >
      <div className="flex items-start gap-4">
        <div
          className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
            isActive
              ? 'bg-med-light/80 scale-110'
              : 'bg-slate-100'
          }`}
        >
          <Icon
            className={`w-5 h-5 transition-colors duration-300 ${
              isActive ? 'text-med-accent' : 'text-slate-400'
            }`}
          />
        </div>
        <div className="flex-1 min-w-0">
          <h3
            className={`text-base font-medium transition-colors duration-200 font-heading tracking-[-0.015em] ${
              isActive ? 'text-med-navy' : 'text-slate-500'
            }`}
          >
            {feature.title}
          </h3>

          <motion.div
            initial={false}
            animate={{
              opacity: isActive ? 1 : 0,
              height: isActive ? 'auto' : 0,
              marginTop: isActive ? 6 : 0,
            }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="overflow-hidden"
          >
            <p className="text-sm text-med-navy/50 leading-[1.6] font-normal">
              {feature.description}
            </p>
            <span
              className={`inline-flex items-center mt-3 px-2.5 py-1 rounded-full border text-[10px] font-medium uppercase tracking-[0.06em] ${feature.badgeClass}`}
            >
              {feature.badgeLabel}
            </span>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

/* ─── Stacked Isometric Boxes ───────────────────────────────────────── */
function StackedIsometricBoxes({ activeId }: { activeId: number }) {
  return (
    <div className="relative flex w-full flex-col items-center">
      {features.map((feature, idx) => {
        const isActive = feature.id === activeId;

        return (
          <motion.div
            key={feature.id}
            initial={false}
            animate={{ opacity: isActive ? 1 : 0.65 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="flex h-[72px] items-center justify-center"
            style={{
              zIndex: isActive ? 10 : features.length - idx,
              marginTop: idx === 0 ? 0 : 10,
            }}
          >
            <IsometricBox
              variant={feature.variant}
              isActive={isActive}
              accentColor={feature.accentColor}
            />
          </motion.div>
        );
      })}
    </div>
  );
}

/* ─── Isometric Box SVG ─────────────────────────────────────────────── */
const IsometricBox = ({
  variant = 'top',
  isActive,
  accentColor,
}: {
  variant: 'top' | 'left' | 'right';
  isActive?: boolean;
  accentColor: string;
}) => {
  const TRANSITION = { type: 'spring' as const, stiffness: 300, damping: 30 };

  const YVariants = {
    animate: { translateY: -20 },
    initial: { translateY: 0 },
  };
  const XVariants = {
    animate: { translateX: 20 },
    initial: { translateX: 0 },
  };
  const NegativeXVariants = {
    animate: { translateX: -20 },
    initial: { translateX: 0 },
  };
  const NoOpVariants = {
    animate: { translateX: 0, translateY: 0 },
    initial: { translateX: 0, translateY: 0 },
  };

  const getVariants = (face: 'top' | 'left' | 'right') => {
    if (face !== variant) return NoOpVariants;
    switch (face) {
      case 'left':
        return NegativeXVariants;
      case 'right':
        return XVariants;
      case 'top':
        return YVariants;
      default:
        return NoOpVariants;
    }
  };

  const defaultStroke = '#e2e8f0'; // slate-200

  const getStrokeColor = (face: 'top' | 'left' | 'right') => {
    if (face !== variant) return defaultStroke;
    if (!isActive) return defaultStroke;
    return accentColor;
  };

  return (
    <motion.div animate={isActive ? 'animate' : 'initial'} initial="initial">
      <motion.svg
        viewBox="0 0 200 200"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="w-60 h-60"
      >
        {/* Top face */}
        <motion.path
          variants={getVariants('top')}
          transition={TRANSITION}
          d="M100 40 Q108 40 155 68 Q162 72 155 76 Q108 104 100 104 Q92 104 45 76 Q38 72 45 68 Q92 40 100 40 Z"
          fill="#fafafe"
          stroke={getStrokeColor('top')}
          strokeWidth="1.5"
        />
        {/* Inner rounded rectangle on top */}
        <motion.path
          variants={getVariants('top')}
          transition={TRANSITION}
          d="M100 52 Q105 52 132 68 Q138 72 132 76 Q105 92 100 92 Q95 92 68 76 Q62 72 68 68 Q95 52 100 52 Z"
          fill="white"
          stroke={getStrokeColor('top')}
          strokeWidth="1"
        />
        {/* Left face */}
        <motion.path
          variants={getVariants('left')}
          transition={TRANSITION}
          d="M45 76 L100 104 L100 164 Q100 170 92 166 L45 140 Q38 136 38 128 L38 80 Q38 72 45 76 Z"
          fill="#f1f5f9"
          stroke={getStrokeColor('left')}
          strokeWidth="1.5"
        />
        {/* Right face */}
        <motion.path
          variants={getVariants('right')}
          transition={TRANSITION}
          d="M155 76 L100 104 L100 164 Q100 170 108 166 L155 140 Q162 136 162 128 L162 80 Q162 72 155 76 Z"
          fill="#fafafe"
          stroke={getStrokeColor('right')}
          strokeWidth="1.5"
        />
        {/* Dashed lines — left face */}
        <motion.path variants={getVariants('left')} transition={TRANSITION} d="M55 86 L55 145" stroke={getStrokeColor('left')} strokeWidth="1" strokeLinecap="round" strokeDasharray="3 3" />
        <motion.path variants={getVariants('left')} transition={TRANSITION} d="M70 95 L70 155" stroke={getStrokeColor('left')} strokeWidth="1" strokeLinecap="round" strokeDasharray="3 3" />
        <motion.path variants={getVariants('left')} transition={TRANSITION} d="M85 104 L85 162" stroke={getStrokeColor('left')} strokeWidth="1" strokeLinecap="round" strokeDasharray="3 3" />
        {/* Dashed lines — right face */}
        <motion.path variants={getVariants('right')} transition={TRANSITION} d="M145 86 L145 145" stroke={getStrokeColor('right')} strokeWidth="1" strokeLinecap="round" strokeDasharray="3 3" />
        <motion.path variants={getVariants('right')} transition={TRANSITION} d="M130 95 L130 155" stroke={getStrokeColor('right')} strokeWidth="1" strokeLinecap="round" strokeDasharray="3 3" />
        <motion.path variants={getVariants('right')} transition={TRANSITION} d="M115 104 L115 162" stroke={getStrokeColor('right')} strokeWidth="1" strokeLinecap="round" strokeDasharray="3 3" />
      </motion.svg>
    </motion.div>
  );
};
